/**
 * Centralized "ticket submission ready" gate.
 *
 * Polls the secure ticket-data endpoint until the ticket has finished both
 * server-side phases of the submission flow:
 *   1. Instant quote generation (the ticket transitions to `approved` and/or
 *      gets a `price` / `selected_quote_id`).
 *   2. Lifestyle image generation (the `image_type='other'` variant required
 *      by `instantQuoteService` before quote persistence completes).
 *
 * Used by every ticket-submission entry point (create page, ticket page
 * Get Instant Quote flows so the loader never releases
 * before the ticket page can render with all assets resolved.
 *
 * Resolves with `true` when both phases are done, `false` if we timed out
 * or aborted. Network errors during polling are swallowed and retried.
 */

import { logger } from "@/utils/logger";

const COMPONENT = "waitForTicketSubmissionReady";

const DEFAULT_EXPECTED_IMAGE_COUNT = 1;
const DEFAULT_TIMEOUT_MS = 180_000;
const DEFAULT_INTERVAL_MS = 3_000;

export interface WaitForTicketSubmissionReadyOptions {
  expectedImageCount?: number;
  timeoutMs?: number;
  intervalMs?: number;
  /** Maximum duration of one ticket-data request within the overall wait. */
  requestTimeoutMs?: number;
  /** When false, skip the image-readiness check (still waits for quote). */
  requireImages?: boolean;
  /** When false, skip the quote-readiness check (still waits for images). */
  requireQuote?: boolean;
  signal?: AbortSignal;
  /** Called after every poll with whatever phases have completed so far. */
  onProgress?: (progress: {
    quoteReady: boolean;
    imagesReady: boolean;
    imageCount: number;
  }) => void;
}

interface TicketSnapshot {
  status?: string | null;
  price?: number | null;
  selected_quote_id?: string | null;
  other_image_urls?: string[] | null;
}

const QUOTE_READY_STATUSES = new Set([
  "approved",
  "paid",
  "in-production",
  "shipped",
  "delivered",
]);

function isQuoteReady(ticket: TicketSnapshot | undefined | null): boolean {
  if (!ticket) return false;
  if (ticket.status && QUOTE_READY_STATUSES.has(ticket.status)) return true;
  if (typeof ticket.price === "number" && ticket.price > 0) return true;
  if (ticket.selected_quote_id) return true;
  return false;
}

function imageCount(ticket: TicketSnapshot | undefined | null): number {
  const images = ticket?.other_image_urls;
  return Array.isArray(images) ? images.length : 0;
}

export async function waitForTicketSubmissionReady(
  ticketId: string,
  options: WaitForTicketSubmissionReadyOptions = {}
): Promise<boolean> {
  if (!ticketId) return false;

  const {
    expectedImageCount = DEFAULT_EXPECTED_IMAGE_COUNT,
    timeoutMs = DEFAULT_TIMEOUT_MS,
    intervalMs = DEFAULT_INTERVAL_MS,
    requestTimeoutMs = 10_000,
    requireImages = true,
    requireQuote = true,
    signal,
    onProgress,
  } = options;

  const startedAt = Date.now();
  const deadline = startedAt + timeoutMs;

  let lastQuoteReady = false;
  let lastImagesReady = false;
  let lastImageCount = 0;
  let pollIndex = 0;

  logger.info("Ticket submission readiness wait started", {
    component: COMPONENT,
    metadata: {
      ticketId,
      requireQuote,
      requireImages,
      expectedImageCount,
      timeoutMs,
      intervalMs,
    },
  });

  while (Date.now() < deadline) {
    if (signal?.aborted) {
      logger.info("Ticket submission readiness wait aborted via signal", {
        component: COMPONENT,
        metadata: { ticketId, pollIndex, elapsedMs: Date.now() - startedAt },
      });
      return false;
    }

    pollIndex += 1;

    try {
      const requestController = new AbortController();
      const forwardAbort = () => requestController.abort(signal?.reason);
      if (signal?.aborted) forwardAbort();
      else signal?.addEventListener("abort", forwardAbort, { once: true });
      const requestTimer = setTimeout(
        () => requestController.abort(),
        Math.min(requestTimeoutMs, Math.max(1, deadline - Date.now()))
      );

      let response: Response;
      try {
        response = await fetch("/api/ticket-data", {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ ticketId }),
          signal: requestController.signal,
        });
      } finally {
        clearTimeout(requestTimer);
        signal?.removeEventListener("abort", forwardAbort);
      }

      if (response.ok) {
        const json = (await response.json()) as {
          data?: { ticket?: TicketSnapshot };
        };
        const ticket = json?.data?.ticket;
        const quoteReady = !requireQuote || isQuoteReady(ticket);
        const count = imageCount(ticket);
        const imagesReady = !requireImages || count >= expectedImageCount;

        lastQuoteReady = quoteReady;
        lastImagesReady = imagesReady;
        lastImageCount = count;

        // Visible progress log so we can see polling state in dev tools and
        // diagnose premature releases / runaway polls.
        logger.info("Ticket submission readiness poll", {
          component: COMPONENT,
          metadata: {
            ticketId,
            pollIndex,
            elapsedMs: Date.now() - startedAt,
            quoteReady,
            imagesReady,
            imageCount: count,
            ticketStatus: ticket?.status ?? null,
            ticketPrice: ticket?.price ?? null,
            hasSelectedQuoteId: Boolean(ticket?.selected_quote_id),
          },
        });

        onProgress?.({ quoteReady, imagesReady, imageCount: count });

        if (quoteReady && imagesReady) {
          logger.info("Ticket submission ready", {
            component: COMPONENT,
            metadata: {
              ticketId,
              pollIndex,
              elapsedMs: Date.now() - startedAt,
              imageCount: count,
            },
          });
          return true;
        }
      } else {
        logger.warn("Ticket submission readiness poll non-ok response", {
          component: COMPONENT,
          metadata: {
            ticketId,
            pollIndex,
            status: response.status,
          },
        });
      }
    } catch (err) {
      if (signal?.aborted) return false;
      // Swallow transient network/abort errors and retry until the deadline.
      logger.debug("Ticket submission readiness poll errored, retrying", {
        component: COMPONENT,
        metadata: {
          ticketId,
          pollIndex,
          error: err instanceof Error ? err.message : String(err),
        },
      });
    }

    const remaining = deadline - Date.now();
    if (remaining <= 0) break;
    await new Promise(resolve =>
      setTimeout(resolve, Math.min(intervalMs, remaining))
    );
  }

  logger.warn("Ticket submission readiness wait timed out", {
    component: COMPONENT,
    metadata: {
      ticketId,
      elapsedMs: Date.now() - startedAt,
      quoteReady: lastQuoteReady,
      imagesReady: lastImagesReady,
      imageCount: lastImageCount,
      expectedImageCount,
    },
  });
  return false;
}

/**
 * @deprecated Prefer {@link waitForTicketSubmissionReady}. Kept as a thin
 * wrapper for callers that only care about the lifestyle-image phase.
 */
export async function waitForTicketLifestyleImages(
  ticketId: string,
  options: Omit<
    WaitForTicketSubmissionReadyOptions,
    "requireQuote" | "requireImages"
  > & { expectedCount?: number } = {}
): Promise<boolean> {
  const { expectedCount, ...rest } = options;
  return waitForTicketSubmissionReady(ticketId, {
    ...rest,
    requireQuote: false,
    requireImages: true,
    expectedImageCount: expectedCount,
  });
}
